import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageTitleScript from "@/components/MetaScript";
import { planApi } from "@/api/planApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useInvalidateUserInfo } from "@/hooks/useUserInfo";
import { LoadingModal } from "@/components/ui/LoadingModal";
import {
  cancellationReasons,
  cancellationAlertMessage,
} from "@/config/subscription";

export function SettingsSubscriptionCancelPage() {
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useToastHandler();
  const invalidateUserInfo = useInvalidateUserInfo();
  const [cancellation_reason, setCustomerCancellationReason] =
    useState<string>("other");
  const [cancellation_comment, setCustomerCancellationComment] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleBack = () => {
    navigate("/settings/subscription", { replace: true });
  };

  const handleConfirmCancellation = async () => {
    if (!cancellation_reason) return;

    try {
      setIsLoading(true);

      const requestData = {
        cancellation_reason,
        ...(cancellation_comment.trim() && {
          cancellation_comment: cancellation_comment.trim(),
        }),
      };

      const response = await planApi.cancelSubscription(requestData);

      // Show success message
      handleSuccess(response.message);

      // Invalidate user info to refresh subscription status
      invalidateUserInfo();

      // Redirect back to subscription page
      navigate("/settings/subscription", { replace: true });
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <PageTitleScript title="Cancel Subscription" />

      <div className="container mx-auto px-8 py-10 max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to subscription
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Confirm Cancellation
          </h1>
        </div>

        <h3 className="text-lg font-semibold mb-2">Select a reason:</h3>
        {/* Cancellation Reasons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          {cancellationReasons.map((reason) => (
            <button
              key={reason.key}
              onClick={() => setCustomerCancellationReason(reason.key)}
              className={`w-full bg-gray-50 rounded-sm px-4 py-2 border-2 transition-all hover:bg-gray-100 text-left ${
                cancellation_reason === reason.key
                  ? "border-red-500 bg-red-50"
                  : "border-gray-200"
              } `}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-base font-medium text-gray-900">
                    {reason.label}
                  </h3>

                  {reason.key == "other" && (
                    <p className="text-gray-600 text-sm mt-1">
                      {reason.description}
                    </p>
                  )}
                </div>
                {cancellation_reason === reason.key && (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center ml-4">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Additional Comments */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-2">Additional comments</h3>
          <textarea
            value={cancellation_comment}
            onChange={(e) => setCustomerCancellationComment(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="Optional"
            className="w-full bg-white rounded-2xl p-4 border border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors resize-none"
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-gray-600 text-sm">
              Share any additional feedback
            </p>
            <span className="text-gray-600 text-sm">
              {cancellation_comment.length}/1000
            </span>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 p-6 bg-gray-50 rounded-2xl border border-gray-200 mb-8">
          <div className="text-gray-600 text-base space-y-2">
            <h3 className="text-lg font-semibold mb-2">Important:</h3>
            {cancellationAlertMessage.map((point, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                <p dangerouslySetInnerHTML={{ __html: point }}></p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button
            onClick={handleConfirmCancellation}
            disabled={!cancellation_reason || isLoading}
            variant="destructive"
            size="xl"
            className="w-full"
          >
            Confirm Cancellation
          </Button>

          <Button
            onClick={handleBack}
            variant="outline"
            size="xl"
            className="w-full"
          >
            Maybe later
          </Button>
        </div>
      </div>

      {/* Loading Modal */}
      <LoadingModal isOpen={isLoading} message="Cancelling subscription..." />
    </div>
  );
}
